// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from './component';

/**
 * @param {Entry} entry
 * @return {Promise}
 */
const readEntry = entry => new Promise(resolve => {
    if (entry.isDirectory) {
        entry.createReader().readEntries(function (entries) {
            resolve(entriesToFiles(entries));
        });
    } else {
        entry.file(resolve);
    }
});

/**
 * @param {String[]} entries
 * @return {Promise}
 */
const entriesToFiles = entries => Promise.all(entries.map(readEntry))
    .then(items => {
        let files = [];
        items.forEach(function (item) {
            if (Array.isArray(item)) {
                files = files.concat(item);
            } else {
                files.push(item);
            }
        });
        return files;
    });

/**
 * @param {File} file
 * @return {Promise}
 */
const isFile = file => new Promise(resolve => {
    if (file.size > 4096) {
        resolve(true);
        return;
    }

    if (!window.FileReader
        || (window.opera && navigator.platform.toLowerCase().indexOf('mac') > -1 && window.opera.version() === '12.00')
    ) {
        resolve(null);
    } else {
        try {
            const reader = new FileReader();
            reader.onerror = () => {
                reader.onloadend = null;
                reader.onprogress = null;
                reader.onerror = null;
                resolve(false);
            };
            reader.onprogress = e => {
                reader.onloadend = null;
                reader.onprogress = null;
                reader.onerror = null;
                if (e.type !== 'loadend') {
                    reader.abort();
                }
                resolve(true);
            };
            reader.onloadend = reader.onprogress;
            reader.readAsDataURL(file);
        } catch (e) {
            resolve(false);
        }
    }
});

/**
 * @param {Event} e
 * @return {Boolean}
 */
const isDragFiles = e => {
    if (!e.dataTransfer) {
        return false;
    }

    if (e.dataTransfer.effectAllowed === 'none') {
        return false;
    }

    return e.dataTransfer.files
        || (!Prototype.Browser.WebKit && e.dataTransfer.types.contains && e.dataTransfer.types.contains('Files'));
};

/**
 * @param {Event} e
 * @return {Promise}
 */
const getDragFiles = e => {
    if (!e.dataTransfer) {
        return Promise.resolve([]);
    }

    if (!e.dataTransfer.items) {
        return Promise.all([].map.call(e.dataTransfer.files, isFile))
            .then(isFiles => [].filter.call(e.dataTransfer.files, (file, index) => isFiles[index]));
    }

    return entriesToFiles(
        [].map.call(e.dataTransfer.items, item => item.webkitGetAsEntry())
            .filter(entry => !!entry)
    );
};

/**
 * @class Jsw.DropArea
 * Drop area component
 * @extends Jsw.Component
 */
export const DropArea = (function () {
    return Class.create(Component, {
        _initConfiguration($super, config) {
            config = Object.extend({
                cls: 'fm-drop-area',
            }, config || {});
            $super(config);

            this._onDrop = this._getConfigParam('onDrop');
        },

        _initComponentElement($super) {
            $super();

            this._componentElement.update(
                '<div class="fm-drop-area-wrap">' +
                    `<span class="fm-drop-area-text">${this.lmsg('dragAndDropArea')}</span>` +
                '</div>'
            );

            this.hide();
        },

        _addEvents() {
            this._addDocumentHandlers();
            this._addDropAreaHandlers();
        },

        _addDocumentHandlers() {
            $(document).observe('dragover', function (e) {
                if (e.dataTransfer) {
                    e.preventDefault();
                }
            });

            $(document).observe('dragenter', function (e) {
                if (!isDragFiles(e)) {
                    return;
                }
                this.show();
            }.bindAsEventListener(this));

            $(document).observe('drop', function (e) {
                if (!isDragFiles(e)) {
                    return;
                }
                this.hide();
                e.preventDefault();
            }.bindAsEventListener(this));
        },

        _addDropAreaHandlers() {
            this._componentElement.observe('dragover', function (e) {
                if (!isDragFiles(e)) {
                    return;
                }
                e.stopPropagation();
                e.preventDefault();
            }.bindAsEventListener(this));

            this._componentElement.observe('drop', function (e) {
                if (!isDragFiles(e)) {
                    return;
                }
                e.preventDefault();

                this.hide();
                if (this._onDrop) {
                    getDragFiles(e).then(this._onDrop);
                }
            }.bindAsEventListener(this));

            this._componentElement.observe('mouseover', function () {
                this.hide();
            }.bind(this));
        },
    });
}());



// WEBPACK FOOTER //
// ./jsw/drop-area.js